import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { api } from '../lib/api';

// ─── Páginas/módulos do sistema ────────────────────────────────────────────
export const MODULOS = [
    { id: 'dashboard',    label: 'Painel'               },
    { id: 'chao-fabrica', label: 'Chão de Fábrica'      },
    { id: 'ordens',       label: 'Ordens de Produção'   },
    { id: 'producao',     label: 'Produção'             },
    { id: 'maquinarios',  label: 'Maquinário'           },
    { id: 'manutencao',   label: 'Manutenção'           },
    { id: 'alertas',      label: 'Alertas'              },
    { id: 'relatorios',   label: 'Relatórios'           },
    { id: 'equipe',       label: 'Equipe'               },
    { id: 'configuracoes',label: 'Configurações'        },
    { id: 'usuarios',     label: 'Gestão de Usuários'  },
    // Planejamento diario desativado temporariamente para a implantacao em producao.
    // { id: 'planejamento', label: 'Planejamento Diário' },
];

const useAuthStore = create(
    persist(
        (set, get) => ({
            // ─── Estado ────────────────────────────────────────────────────
            operadorLogado: null,
            token: null,
            sessaoExpirada: false,
            // Última máquina escolhida NESTE tablet (04/08/2026) — não vem mais do
            // login (isso agora vem dos centros de trabalho do usuário, ver
            // Gestão de Usuários); só serve pra pré-selecionar a opção certa na
            // telinha de escolha de máquina (Producao.jsx) quando o usuário tem
            // mais de uma candidata. Persiste por aparelho, `logout` não limpa.
            maquinaSelecionada: null,

            // ─── Login ─────────────────────────────────────────────────────
            // Sem máquina aqui — vem do(s) centro(s) de trabalho do usuário
            // (Z_USER1.U1_CTRAB, configurado na Gestão de Usuários). Se só existir
            // uma máquina candidata, o backend já resolve sozinho (user.maquina
            // preenchido); se houver mais de uma, fica null e Producao.jsx pergunta.
            login: async (usuario, senha) => {
                try {
                    const resposta = await api.post('/api/auth/login', { usuario, senha });
                    if (!resposta?.token || !resposta?.user) {
                        return { ok: false, erro: 'Usuário ou senha inválidos.' };
                    }
                    const { token, user } = resposta;
                    set((state) => ({
                        token,
                        operadorLogado: { ...user, perfil: user.papel },
                        maquinaSelecionada: user.maquina ? { codigo: user.maquina, nome: user.maquinaNome } : state.maquinaSelecionada,
                    }));
                    return { ok: true };
                } catch (err) {
                    return { ok: false, erro: err.message || 'Usuário ou senha inválidos.' };
                }
            },

            // Troca de senha obrigatória (29/08/2026, pedido do usuário) —
            // chamado pela tela TrocarSenhaPrimeiroAcesso.jsx depois que o
            // endpoint confirma a troca; só limpa a flag local, o backend já
            // zerou ZU1_TROCAR.
            limparDeveTrocarSenha: () => {
                set((state) => ({
                    operadorLogado: state.operadorLogado ? { ...state.operadorLogado, deveTrocarSenha: false } : state.operadorLogado,
                }));
            },

            // Chamado pela telinha de escolha de máquina (Producao.jsx) quando o
            // usuário tem mais de uma máquina candidata e precisa escolher.
            definirMaquina: (codigo, nome) => {
                set((state) => ({
                    operadorLogado: state.operadorLogado ? { ...state.operadorLogado, maquina: codigo, maquinaNome: nome } : state.operadorLogado,
                    maquinaSelecionada: { codigo, nome },
                }));
            },

            // "Trocar máquina" (04/08/2026) — escape hatch pro operador voltar pra
            // telinha de escolha quando a máquina atual não tem nenhuma OP em
            // aberto (ver ModalSelecionarOP em Producao.jsx). Limpa só a máquina
            // resolvida, não o login inteiro.
            limparMaquina: () => {
                set((state) => ({
                    operadorLogado: state.operadorLogado ? { ...state.operadorLogado, maquina: null, maquinaNome: null } : state.operadorLogado,
                }));
            },

            // Atualiza a lista de máquinas candidatas com dado FRESCO do
            // servidor (13/08/2026, GET /api/producao/maquinas-disponiveis)
            // — chamado por "Trocar de máquina" (Producao.jsx). Sem isso, a
            // lista ficava presa no retrato tirado no login (até 8h
            // desatualizada — máquina podia ganhar/perder OP no meio do
            // turno sem o operador nunca ver isso sem deslogar de novo).
            atualizarMaquinasDisponiveis: (maquinasDisponiveis, totalMaquinasCentroTrabalho) => {
                set((state) => ({
                    operadorLogado: state.operadorLogado
                        ? { ...state.operadorLogado, maquinasDisponiveis, totalMaquinasCentroTrabalho }
                        : state.operadorLogado,
                }));
            },

            // ─── Logout ────────────────────────────────────────────────────
            // Não mexe em maquinaSelecionada de propósito — o tablet continua na
            // mesma máquina física depois do logout, só troca quem está logado.
            logout: () => set({ operadorLogado: null, token: null, sessaoExpirada: false }),

            // Chamado pelo interceptor de 401 em lib/api.js (token expirado/inválido),
            // não pelo botão "Sair" — sessaoExpirada liga o aviso na tela de Login.
            logoutPorExpiracao: () => set({ operadorLogado: null, token: null, sessaoExpirada: true }),
            limparSessaoExpirada: () => set({ sessaoExpirada: false }),

            // ─── Verifica permissão ────────────────────────────────────────
            temPermissao: (moduloId) => {
                const op = get().operadorLogado;
                if (!op) return false;
                if (op.perfil === 'admin') return true;
                return op.permissoes.includes(moduloId);
            },
        }),
        {
            name: 'meditec-auth',
            version: 2,
            partialize: (state) => ({
                operadorLogado: state.operadorLogado,
                token: state.token,
                maquinaSelecionada: state.maquinaSelecionada,
            }),
        }
    )
);

export default useAuthStore;
